import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { sortByDateDescending } from '../../utils/dateUtils';
import { HistoryEntry } from './HistorySideDrawer';

export interface UseHistoryDrawerConfig {
    documentType: string;
    fetchHistoryFn: (params: { documentType: string }) => Promise<HistoryEntry[]>;
    fetchVersionFn: (docId: string, params: { documentType: string }) => Promise<any>;
    onVersionLoaded?: (data: any, shouldSave: boolean, entryId?: string, isInitialLoad?: boolean) => void;
    onDrawerClose?: () => void;
}

export interface UseHistoryDrawerReturn {
    isOpen: boolean;
    isExpanded: boolean;
    loading: boolean;
    previewLoading: boolean;
    historyEntries: HistoryEntry[];
    openDrawer: () => Promise<void>;
    closeDrawer: () => void;
    toggleDrawer: () => void;
    toggleExpand: () => void;
    selectHistoryEntry: (entry: HistoryEntry) => Promise<void>;
    previewHistoryEntry: (entry: HistoryEntry) => Promise<void>;
    reloadHistory: () => Promise<void>;
}

// ==================== CONSTANTS ====================
const ANIMATION_DELAY = 300;

// ==================== HOOK ====================
export function useHistoryDrawer({
    documentType,
    fetchHistoryFn,
    fetchVersionFn,
    onVersionLoaded,
    onDrawerClose,
}: UseHistoryDrawerConfig): UseHistoryDrawerReturn {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);

    const fetchAndSetHistory = useCallback(async (): Promise<HistoryEntry[]> => {
        const entries = await fetchHistoryFn({ documentType });
        const sortedEntries = sortByDateDescending(entries);
        setHistoryEntries(sortedEntries);
        return sortedEntries;
    }, [fetchHistoryFn, documentType]);

    const openDrawer = useCallback(async () => {
        setIsOpen(true);
        setLoading(true);

        try {
            const sortedEntries = await fetchAndSetHistory();

            // Auto-select the current version (first entry) — metadata only, no
            // fetchVersionFn: the editor already shows the latest content, and
            // every consumer's isInitialLoad branch uses just the entry id (to
            // highlight the row). Fetching here downloaded + parsed the whole
            // latest version only to be discarded.
            const currentVersion = sortedEntries?.[0];
            if (currentVersion) {
                onVersionLoaded?.(null, false, currentVersion._id, true);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error('Failed to load history');
            setHistoryEntries([]);
        } finally {
            setLoading(false);
        }
    }, [fetchAndSetHistory, fetchVersionFn, documentType, onVersionLoaded]);

    const closeDrawer = useCallback(() => {
        onDrawerClose?.();
        setIsOpen(false);
        setTimeout(() => {
            setHistoryEntries([]);
            setIsExpanded(false);
        }, ANIMATION_DELAY);
    }, [onDrawerClose]);

    const toggleDrawer = useCallback(async () => {
        if (isOpen) {
            closeDrawer();
        } else {
            await openDrawer();
        }
    }, [isOpen, openDrawer, closeDrawer]);

    const toggleExpand = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const loadVersion = useCallback(
        async (entry: HistoryEntry, shouldSave: boolean) => {
            setPreviewLoading(true);

            try {
                const versionData = await fetchVersionFn(entry._id, { documentType });
                onVersionLoaded?.(versionData, shouldSave, entry._id);
            } catch (error) {
                console.error(`Error ${shouldSave ? 'restoring' : 'loading'} history version:`, error);
                toast.error(`Failed to ${shouldSave ? 'restore' : 'load'} version`);
            } finally {
                setPreviewLoading(false);
            }
        },
        [fetchVersionFn, documentType, onVersionLoaded],
    );

    const selectHistoryEntry = useCallback((entry: HistoryEntry) => loadVersion(entry, true), [loadVersion]);

    const previewHistoryEntry = useCallback((entry: HistoryEntry) => loadVersion(entry, false), [loadVersion]);

    const reloadHistory = useCallback(async () => {
        if (!isOpen) return;

        setLoading(true);
        try {
            await fetchAndSetHistory();
        } catch (error) {
            console.error('Error reloading history:', error);
            toast.error('Failed to reload history');
        } finally {
            setLoading(false);
        }
    }, [isOpen, fetchAndSetHistory]);

    return {
        isOpen,
        isExpanded,
        loading,
        previewLoading,
        historyEntries,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        toggleExpand,
        selectHistoryEntry,
        previewHistoryEntry,
        reloadHistory,
    };
}
